/**
 * Reading a run record honestly, for every screen that reports one.
 *
 * A new-seed misting record is not shaped like a session run, and the two
 * places that difference bites are here so no screen has to remember it:
 *
 *  - Its `plan` describes the WHOLE day's program (every cycle), but only the
 *    cycles that really fired leave a pulse. A day of outages completes with no
 *    pulses at all, so falling back to the plan's depth (right for a session
 *    run, whose plan is what the executor commanded) would claim a day's water
 *    that never left the pipe.
 *  - `NEW_SEED` is not a weather decision. The session run exists so history
 *    stays coherent, and it is SKIPPED, but nothing was "skipped" in the sense
 *    a smart-skip count means: the zone was watered, on its own schedule.
 */

import type { RunRecord } from '@irrigation/core';

/** A run written by the new-seed misting scheduler (`seed-<date>#<zone>`). */
export function isSeedRun(run: RunRecord): boolean {
  return run.id.startsWith('seed-');
}

/** True when this session run was skipped only because the zone is being misted. */
export function isNewSeedSkip(run: RunRecord): boolean {
  return run.plan.skips.includes('NEW_SEED');
}

/**
 * Depth this run really applied, inches.
 *
 * `appliedIn` is written once the water is accounted for; the plan's gross
 * depth stands in for a session run that completed before the field existed.
 * A misting record gets no such fallback; see the note above.
 */
export function appliedInches(run: RunRecord): number {
  if (isSeedRun(run)) return run.appliedIn ?? 0;
  return run.appliedIn ?? run.plan.grossDepthIn;
}

/** Valve minutes this run really ran: the pulses for a misting record, the plan otherwise. */
export function valveMinutes(run: RunRecord): number {
  if (!isSeedRun(run)) return run.plan.totalRuntimeMinutes;
  return (run.pulses ?? []).reduce(
    (sum, p) => sum + Math.max(0, Date.parse(p.end) - Date.parse(p.start)) / 60_000,
    0,
  );
}

/**
 * Runs that count as a weather/rules skip: everything SKIPPED except the daily
 * new-seed hand-off.
 */
export function weatherSkips(runs: RunRecord[]): RunRecord[] {
  return runs.filter((r) => r.status === 'SKIPPED' && !isNewSeedSkip(r));
}
